import React, { useState, useEffect } from "react";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { 
  CheckCircle, 
  XCircle, 
  Star, 
  Clock, 
  User, 
  Mail,
  Shield,
  RefreshCw,
  MessageSquare,
  Phone,
  Calendar,
  Send,
  Briefcase,
  Home,
  FileText,
  Trash2,
  AlertTriangle,
  Bed
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const AdminPanel = () => {
  const [reviews, setReviews] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [jobApplications, setJobApplications] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [bedAvailability, setBedAvailability] = useState({
    residential_available: 0,
    nursing_available: 0,
    dementia_available: 0
  });
  const [loading, setLoading] = useState(true);
  const [enquiriesLoading, setEnquiriesLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [complaintsLoading, setComplaintsLoading] = useState(true);
  const [bedLoading, setBedLoading] = useState(false);
  const [reviewFilter, setReviewFilter] = useState("pending");
  const [enquiryFilter, setEnquiryFilter] = useState("all");
  const [activeTab, setActiveTab] = useState("enquiries");
  const [adminPassword, setAdminPassword] = useState("");
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [emailHealth, setEmailHealth] = useState(null);
  
  // Reply Modal State
  const [replyModalOpen, setReplyModalOpen] = useState(false);
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [replyMessage, setReplyMessage] = useState("");
  const [sendingReply, setSendingReply] = useState(false);

  // Delete Confirmation Modal State
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState("");

  const ADMIN_PASSWORD = "barnston2024";

  const enquiryTypes = [
    { value: "all", label: "All Enquiries", icon: MessageSquare, color: "bg-gray-500" },
    { value: "visit", label: "Visit Bookings", icon: Calendar, color: "bg-teal-500" },
    { value: "brochure", label: "Brochure Requests", icon: FileText, color: "bg-blue-500" },
    { value: "speak", label: "Callback Requests", icon: Phone, color: "bg-purple-500" },
    { value: "general", label: "General Enquiries", icon: MessageSquare, color: "bg-orange-500" }
  ];

  const handleLogin = (e) => {
    e.preventDefault();
    if (adminPassword === ADMIN_PASSWORD) {
      setIsAuthenticated(true);
      toast.success("Welcome to the Admin Panel");
    } else {
      toast.error("Incorrect password");
    }
  };

  useEffect(() => {
    if (isAuthenticated) {
      if (activeTab === "reviews") {
        fetchReviews();
      } else if (activeTab === "enquiries") {
        fetchEnquiries();
      } else if (activeTab === "jobs") {
        fetchJobApplications();
        fetchJobRoles();
      } else if (activeTab === "complaints") {
        fetchComplaints();
      } else if (activeTab === "availability") {
        fetchBedAvailability();
      }
    }
  }, [isAuthenticated, reviewFilter, enquiryFilter, activeTab]);

  // Poll email service health so the admin knows immediately when notifications stop.
  useEffect(() => {
    if (!isAuthenticated) return undefined;
    const fetchHealth = async () => {
      try {
        const response = await axios.get(`${BACKEND_URL}/api/email/health`);
        setEmailHealth(response.data);
      } catch (error) {
        console.error("Failed to fetch email health:", error);
        setEmailHealth({ status: "unknown", message: "Could not reach email health endpoint." });
      }
    };
    fetchHealth();
    const id = setInterval(fetchHealth, 60000); // refresh every 60s
    return () => clearInterval(id);
  }, [isAuthenticated]);

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const endpoint = reviewFilter === "pending" 
        ? `${BACKEND_URL}/api/reviews/pending`
        : `${BACKEND_URL}/api/reviews/approved`;
      const response = await axios.get(endpoint);
      setReviews(response.data);
    } catch (error) {
      console.error("Error fetching reviews:", error);
      toast.error("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  const fetchEnquiries = async () => {
    setEnquiriesLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/contact/enquiries?limit=100`);
      let filteredEnquiries = response.data;
      
      if (enquiryFilter !== "all") {
        filteredEnquiries = response.data.filter(e => e.enquiry_type === enquiryFilter);
      }
      
      setEnquiries(filteredEnquiries);
    } catch (error) {
      console.error("Error fetching enquiries:", error);
      toast.error("Failed to fetch enquiries");
    } finally {
      setEnquiriesLoading(false);
    }
  };

  const fetchJobApplications = async () => {
    setJobsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/careers/applications?limit=100`);
      setJobApplications(response.data);
    } catch (error) {
      console.error("Error fetching job applications:", error);
      toast.error("Failed to fetch job applications");
    } finally {
      setJobsLoading(false);
    }
  };

  const fetchJobRoles = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/jobs/roles`);
      setJobRoles(response.data);
    } catch (error) {
      console.error("Error fetching job roles:", error);
    }
  };

  const fetchComplaints = async () => {
    setComplaintsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/complaints`);
      setComplaints(response.data);
    } catch (error) {
      console.error("Error fetching complaints:", error);
      toast.error("Failed to fetch complaints");
    } finally {
      setComplaintsLoading(false);
    }
  };

  const fetchBedAvailability = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/bed-availability`);
      setBedAvailability({
        residential_available: response.data.residential_available,
        nursing_available: response.data.nursing_available,
        dementia_available: response.data.dementia_available
      });
    } catch (error) {
      console.error("Error fetching bed availability:", error);
    }
  };

  const updateBedAvailability = async () => {
    setBedLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/bed-availability`, {
        ...bedAvailability,
        updated_by: "admin"
      });
      toast.success("Bed availability updated successfully");
    } catch (error) {
      console.error("Error updating bed availability:", error);
      toast.error("Failed to update bed availability");
    } finally {
      setBedLoading(false);
    }
  };

  const toggleJobAvailability = async (roleId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/jobs/roles/${roleId}/toggle`);
      toast.success("Job availability updated");
      fetchJobRoles();
    } catch (error) {
      console.error("Error toggling job availability:", error);
      toast.error("Failed to update job availability");
    }
  };

  const handleApprove = async (reviewId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/reviews/${reviewId}/approve?approved_by=admin`);
      toast.success("Review approved successfully");
      fetchReviews();
    } catch (error) {
      console.error("Error approving review:", error);
      toast.error("Failed to approve review");
    }
  };

  const handleReject = async (reviewId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/reviews/${reviewId}/reject`);
      toast.success("Review rejected");
      fetchReviews();
    } catch (error) {
      console.error("Error rejecting review:", error);
      toast.error("Failed to reject review");
    }
  };

  const handleDisapprove = async (reviewId) => {
    try {
      await axios.post(`${BACKEND_URL}/api/reviews/${reviewId}/disapprove`);
      toast.success("Review disapproved and moved back to pending");
      fetchReviews();
    } catch (error) {
      console.error("Error disapproving review:", error);
      toast.error("Failed to disapprove review");
    }
  };

  const openReplyModal = (enquiry) => {
    setSelectedEnquiry(enquiry);
    setReplyMessage("");
    setReplyModalOpen(true);
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error("Please enter a reply message");
      return;
    }

    setSendingReply(true);
    try {
      await axios.post(`${BACKEND_URL}/api/contact/enquiry/${selectedEnquiry.id}/reply`, {
        reply_message: replyMessage,
        replied_by: "admin"
      });
      toast.success("Reply sent successfully!");
      setReplyModalOpen(false);
      setReplyMessage("");
      fetchEnquiries();
    } catch (error) {
      console.error("Error sending reply:", error);
      toast.error("Failed to send reply");
    } finally {
      setSendingReply(false);
    }
  };

  // Delete Functions
  const openDeleteModal = (item, type) => {
    setItemToDelete(item);
    setDeleteType(type);
    setDeleteModalOpen(true);
  };

  const handleDelete = async () => {
    try {
      let endpoint = "";
      if (deleteType === "enquiry") {
        endpoint = `${BACKEND_URL}/api/contact/enquiry/${itemToDelete.id}`;
      } else if (deleteType === "review") {
        endpoint = `${BACKEND_URL}/api/reviews/${itemToDelete.id}`;
      } else if (deleteType === "job") {
        endpoint = `${BACKEND_URL}/api/careers/application/${itemToDelete.id}`;
      } else if (deleteType === "complaint") {
        endpoint = `${BACKEND_URL}/api/complaints/${itemToDelete.id}`;
      }

      await axios.delete(endpoint);
      toast.success(`${deleteType.charAt(0).toUpperCase() + deleteType.slice(1)} deleted successfully`);
      setDeleteModalOpen(false);
      setItemToDelete(null);
      
      // Refresh the relevant list
      if (deleteType === "enquiry") fetchEnquiries();
      else if (deleteType === "review") fetchReviews();
      else if (deleteType === "job") fetchJobApplications();
      else if (deleteType === "complaint") fetchComplaints();
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error("Failed to delete item");
    }
  };

  const getEnquiryTypeInfo = (type) => {
    const typeInfo = enquiryTypes.find(t => t.value === type) || enquiryTypes[4];
    return typeInfo;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-2 border-teal-200 shadow-xl">
          <CardContent className="pt-8 pb-8">
            <div className="text-center mb-6">
              <div className="bg-teal-100 text-teal-600 p-4 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                <Shield className="h-10 w-10" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Panel</h1>
              <p className="text-gray-600">Barnston Court Nursing Home</p>
            </div>
            
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <Input
                  type="password"
                  placeholder="Enter admin password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="text-center"
                  data-testid="admin-password-input"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full bg-teal-600 hover:bg-teal-700"
                data-testid="admin-login-btn"
              >
                Access Admin Panel
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-white">
      {/* Header */}
      <div className="bg-teal-700 text-white py-6 px-4">
        <div className="container mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold">Admin Panel</h1>
              <p className="text-teal-200">Barnston Court Nursing Home</p>
            </div>
            <Button 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-teal-700"
              onClick={() => window.location.href = '/'}
            >
              Back to Website
            </Button>
          </div>
        </div>
      </div>

      {/* Email service health banner — surfaces SendGrid outages loudly */}
      {emailHealth && emailHealth.status !== "ok" && emailHealth.status !== "unknown" && (
        <div
          className={`px-4 py-3 border-b-2 ${
            emailHealth.status === "unconfigured"
              ? "bg-amber-50 border-amber-300"
              : "bg-red-50 border-red-300"
          }`}
          data-testid="admin-email-health-banner"
        >
          <div className="container mx-auto flex items-start gap-3">
            <AlertTriangle
              className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
                emailHealth.status === "unconfigured" ? "text-amber-600" : "text-red-600"
              }`}
            />
            <div className="flex-1 text-sm">
              <p className={`font-bold ${emailHealth.status === "unconfigured" ? "text-amber-900" : "text-red-900"}`}>
                {emailHealth.status === "unconfigured"
                  ? "Email service is not configured"
                  : "Email notifications are FAILING"}
              </p>
              <p className={emailHealth.status === "unconfigured" ? "text-amber-800" : "text-red-800"}>
                All submissions are still being saved in the tabs below, but no email notifications
                are being sent to <strong>{emailHealth.notification_email}</strong>.
                {emailHealth.message && (
                  <> Cause: <span className="font-mono text-xs">{emailHealth.message}</span></>
                )}
                {emailHealth.consecutive_failures > 0 && (
                  <> Consecutive failures: <strong>{emailHealth.consecutive_failures}</strong>.</>
                )}
              </p>
              <p className="text-xs mt-1 text-gray-700">
                Common causes: Resend API key revoked/rotated, sender domain no longer verified, or monthly quota exceeded.
                Check the Resend dashboard, then update <code>RESEND_API_KEY</code> in the backend environment.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Main Tab Navigation */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="container mx-auto px-4">
          <div className="flex gap-4 overflow-x-auto">
            <button
              onClick={() => setActiveTab("enquiries")}
              className={`py-4 px-6 font-semibold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "enquiries"
                  ? "border-teal-600 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <MessageSquare className="h-4 w-4 inline mr-2" />
              Enquiries
            </button>
            <button
              onClick={() => setActiveTab("complaints")}
              className={`py-4 px-6 font-semibold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "complaints"
                  ? "border-red-600 text-red-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              Complaints
            </button>
            <button
              onClick={() => setActiveTab("jobs")}
              className={`py-4 px-6 font-semibold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "jobs"
                  ? "border-teal-600 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Briefcase className="h-4 w-4 inline mr-2" />
              Jobs
            </button>
            <button
              onClick={() => setActiveTab("availability")}
              className={`py-4 px-6 font-semibold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "availability"
                  ? "border-green-600 text-green-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Bed className="h-4 w-4 inline mr-2" />
              Bed Availability
            </button>
            <button
              onClick={() => setActiveTab("reviews")}
              className={`py-4 px-6 font-semibold border-b-2 transition-colors whitespace-nowrap ${
                activeTab === "reviews"
                  ? "border-teal-600 text-teal-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              <Star className="h-4 w-4 inline mr-2" />
              Reviews
            </button>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        {/* ENQUIRIES TAB */}
        {activeTab === "enquiries" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Enquiry Management</h2>
              <Button 
                onClick={fetchEnquiries} 
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>

            {/* Enquiry Type Filter Tabs */}
            <div className="flex flex-wrap gap-2 mb-6">
              {enquiryTypes.map((type) => {
                const Icon = type.icon;
                const count = type.value === "all" 
                  ? enquiries.length 
                  : enquiries.filter(e => e.enquiry_type === type.value).length;
                
                return (
                  <Button
                    key={type.value}
                    onClick={() => setEnquiryFilter(type.value)}
                    className={`${
                      enquiryFilter === type.value
                        ? type.color + " text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                    }`}
                  >
                    <Icon className="h-4 w-4 mr-2" />
                    {type.label}
                    {type.value !== "all" && (
                      <span className="ml-2 bg-white/20 px-2 py-0.5 rounded-full text-xs">
                        {count}
                      </span>
                    )}
                  </Button>
                );
              })}
            </div>

            {/* Enquiries List */}
            {enquiriesLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-600 mb-4" />
                <p className="text-gray-600">Loading enquiries...</p>
              </div>
            ) : enquiries.length === 0 ? (
              <Card className="border-2 border-gray-200">
                <CardContent className="pt-8 pb-8 text-center">
                  <MessageSquare className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No enquiries found.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {enquiries.map((enquiry) => {
                  const typeInfo = getEnquiryTypeInfo(enquiry.enquiry_type);
                  const TypeIcon = typeInfo.icon;
                  
                  return (
                    <Card 
                      key={enquiry.id} 
                      className="border-2 border-gray-200 hover:border-teal-300 transition-colors"
                      data-testid={`enquiry-card-${enquiry.id}`}
                    >
                      <CardContent className="pt-6">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                          {/* Enquiry Content */}
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3">
                              <Badge className={`${typeInfo.color} text-white`}>
                                <TypeIcon className="h-3 w-3 mr-1" />
                                {typeInfo.label}
                              </Badge>
                              <Badge className={
                                enquiry.status === "new" 
                                  ? "bg-orange-100 text-orange-700" 
                                  : enquiry.status === "contacted"
                                  ? "bg-blue-100 text-blue-700"
                                  : "bg-green-100 text-green-700"
                              }>
                                {enquiry.status === "new" ? "New" : enquiry.status === "contacted" ? "Contacted" : enquiry.status}
                              </Badge>
                              <span className="text-sm text-gray-500">
                                {formatDate(enquiry.created_at)}
                              </span>
                            </div>

                            <div className="grid md:grid-cols-2 gap-4 mb-4">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-teal-600" />
                                <span className="font-semibold">{enquiry.name}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <Phone className="h-4 w-4 text-teal-600" />
                                <a href={`tel:${enquiry.phone}`} className="text-teal-600 hover:underline">
                                  {enquiry.phone}
                                </a>
                              </div>
                              <div className="flex items-center gap-2">
                                <Mail className="h-4 w-4 text-teal-600" />
                                <a href={`mailto:${enquiry.email}`} className="text-teal-600 hover:underline">
                                  {enquiry.email}
                                </a>
                              </div>
                              {enquiry.care_type && (
                                <div className="flex items-center gap-2">
                                  <Home className="h-4 w-4 text-teal-600" />
                                  <span className="capitalize">{enquiry.care_type} Care</span>
                                </div>
                              )}
                            </div>

                            {enquiry.urgency && (
                              <div className="mb-3">
                                <Badge variant="outline" className="text-sm">
                                  <Clock className="h-3 w-3 mr-1" />
                                  Urgency: {enquiry.urgency === "urgent" ? "Within 1 week" : 
                                           enquiry.urgency === "soon" ? "1-4 weeks" :
                                           enquiry.urgency === "planning" ? "1-3 months" : "Exploring"}
                                </Badge>
                              </div>
                            )}

                            {enquiry.message && (
                              <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-teal-500">
                                <p className="text-gray-700 text-sm">{enquiry.message}</p>
                              </div>
                            )}
                          </div>

                          {/* Actions */}
                          <div className="flex lg:flex-col gap-2">
                            <Button
                              onClick={() => openReplyModal(enquiry)}
                              className="bg-teal-600 hover:bg-teal-700 text-white"
                              data-testid={`reply-btn-${enquiry.id}`}
                            >
                              <Send className="h-4 w-4 mr-2" />
                              Reply
                            </Button>
                            <a
                              href={`mailto:${enquiry.email}`}
                              className="inline-flex items-center justify-center px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md transition-colors"
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Email
                            </a>
                            <a
                              href={`tel:${enquiry.phone}`}
                              className="inline-flex items-center justify-center px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-md transition-colors"
                            >
                              <Phone className="h-4 w-4 mr-2" />
                              Call
                            </a>
                            <Button
                              onClick={() => openDeleteModal(enquiry, "enquiry")}
                              variant="outline"
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`delete-enquiry-btn-${enquiry.id}`}
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </Button>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* JOBS TAB */}
        {activeTab === "jobs" && (
          <div>
            {/* Job Roles Management Section */}
            <Card className="mb-8 border-2 border-purple-200">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                  <Briefcase className="h-5 w-5 text-purple-600" />
                  Manage Available Positions
                </h3>
                <p className="text-gray-600 mb-4 text-sm">Toggle positions on/off to control which jobs appear on the website careers page.</p>
                
                <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                  {jobRoles.map((role) => (
                    <div 
                      key={role.id}
                      className={`flex items-center justify-between p-3 rounded-lg border-2 transition-colors ${
                        role.is_available 
                          ? "bg-green-50 border-green-200" 
                          : "bg-gray-50 border-gray-200"
                      }`}
                    >
                      <span className={`font-medium text-sm ${role.is_available ? "text-green-700" : "text-gray-500"}`}>
                        {role.title}
                      </span>
                      <button
                        onClick={() => toggleJobAvailability(role.id)}
                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${
                          role.is_available 
                            ? "bg-green-500 text-white hover:bg-green-600" 
                            : "bg-gray-300 text-gray-600 hover:bg-gray-400"
                        }`}
                      >
                        {role.is_available ? "ON" : "OFF"}
                      </button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Job Applications</h2>
              <Button 
                onClick={fetchJobApplications} 
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>

            {/* Job Applications List */}
            {jobsLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-600 mb-4" />
                <p className="text-gray-600">Loading job applications...</p>
              </div>
            ) : jobApplications.length === 0 ? (
              <Card className="border-2 border-gray-200">
                <CardContent className="pt-8 pb-8 text-center">
                  <Briefcase className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No job applications found.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {jobApplications.map((application) => (
                  <Card 
                    key={application.id} 
                    className="border-2 border-gray-200 hover:border-teal-300 transition-colors"
                    data-testid={`job-card-${application.id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        {/* Application Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Badge className="bg-purple-500 text-white">
                              <Briefcase className="h-3 w-3 mr-1" />
                              {application.role || "General Application"}
                            </Badge>
                            <Badge className={
                              application.status === "new" 
                                ? "bg-orange-100 text-orange-700" 
                                : application.status === "reviewed"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-green-100 text-green-700"
                            }>
                              {application.status || "New"}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {formatDate(application.created_at)}
                            </span>
                          </div>

                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-teal-600" />
                              <span className="font-semibold">{application.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-teal-600" />
                              <a href={`tel:${application.phone}`} className="text-teal-600 hover:underline">
                                {application.phone}
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-teal-600" />
                              <a href={`mailto:${application.email}`} className="text-teal-600 hover:underline">
                                {application.email}
                              </a>
                            </div>
                            {application.experience && (
                              <div className="flex items-center gap-2">
                                <Clock className="h-4 w-4 text-teal-600" />
                                <span>Experience: {application.experience}</span>
                              </div>
                            )}
                          </div>

                          {application.message && (
                            <div className="bg-gray-50 rounded-lg p-4 border-l-4 border-purple-500">
                              <p className="text-sm text-gray-500 mb-1 font-medium">About the applicant:</p>
                              <p className="text-gray-700 text-sm">{application.message}</p>
                            </div>
                          )}

                          {application.cv_filename && (
                            <div className="mt-3 inline-flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-800 px-3 py-1.5 rounded-md text-xs">
                              <FileText className="h-3.5 w-3.5" />
                              <span className="font-medium">CV attached:</span>
                              <span className="truncate max-w-[200px]">{application.cv_filename}</span>
                            </div>
                          )}
                        </div>

                        {/* Actions */}
                        <div className="flex lg:flex-col gap-2">
                          {application.cv_filename && (
                            <a
                              href={`${BACKEND_URL}/api/careers/application/${application.id}/cv`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="inline-flex items-center justify-center px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-md transition-colors"
                              data-testid={`download-cv-btn-${application.id}`}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Download CV
                            </a>
                          )}
                          <a
                            href={`mailto:${application.email}?subject=Re: Your Application for ${application.role} at Barnston Court`}
                            className="inline-flex items-center justify-center px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-md transition-colors"
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            Reply
                          </a>
                          <a
                            href={`tel:${application.phone}`}
                            className="inline-flex items-center justify-center px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-md transition-colors"
                          >
                            <Phone className="h-4 w-4 mr-2" />
                            Call
                          </a>
                          <Button
                            onClick={() => openDeleteModal(application, "job")}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`delete-job-btn-${application.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* BED AVAILABILITY TAB */}
        {activeTab === "availability" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Bed Availability Management</h2>
              <Button 
                onClick={fetchBedAvailability} 
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>

            <Card className="border-2 border-green-200 max-w-2xl">
              <CardContent className="pt-6">
                <p className="text-gray-600 mb-6">
                  Update the number of available beds for each care type. Changes will be reflected immediately on the website.
                </p>

                <div className="space-y-6">
                  {/* End of Life Care */}
                  <div className="flex items-center justify-between p-4 bg-teal-50 rounded-lg border border-teal-200">
                    <div className="flex items-center gap-3">
                      <div className="bg-teal-100 p-2 rounded-lg">
                        <Bed className="h-5 w-5 text-teal-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">End of Life Care</h3>
                        <p className="text-sm text-gray-500">Total capacity: 12 beds</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBedAvailability(prev => ({
                          ...prev,
                          residential_available: Math.max(0, prev.residential_available - 1)
                        }))}
                        className="h-10 w-10 p-0"
                      >
                        -
                      </Button>
                      <Input
                        type="number"
                        min="0"
                        max="12"
                        value={bedAvailability.residential_available}
                        onChange={(e) => setBedAvailability(prev => ({
                          ...prev,
                          residential_available: Math.min(12, Math.max(0, parseInt(e.target.value) || 0))
                        }))}
                        className="w-20 text-center text-lg font-bold"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBedAvailability(prev => ({
                          ...prev,
                          residential_available: Math.min(12, prev.residential_available + 1)
                        }))}
                        className="h-10 w-10 p-0"
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  {/* Nursing Care */}
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-3">
                      <div className="bg-blue-100 p-2 rounded-lg">
                        <Bed className="h-5 w-5 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Nursing Care</h3>
                        <p className="text-sm text-gray-500">Total capacity: 10 beds</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBedAvailability(prev => ({
                          ...prev,
                          nursing_available: Math.max(0, prev.nursing_available - 1)
                        }))}
                        className="h-10 w-10 p-0"
                      >
                        -
                      </Button>
                      <Input
                        type="number"
                        min="0"
                        max="10"
                        value={bedAvailability.nursing_available}
                        onChange={(e) => setBedAvailability(prev => ({
                          ...prev,
                          nursing_available: Math.min(10, Math.max(0, parseInt(e.target.value) || 0))
                        }))}
                        className="w-20 text-center text-lg font-bold"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBedAvailability(prev => ({
                          ...prev,
                          nursing_available: Math.min(10, prev.nursing_available + 1)
                        }))}
                        className="h-10 w-10 p-0"
                      >
                        +
                      </Button>
                    </div>
                  </div>

                  {/* Dementia Care */}
                  <div className="flex items-center justify-between p-4 bg-orange-50 rounded-lg border border-orange-200">
                    <div className="flex items-center gap-3">
                      <div className="bg-orange-100 p-2 rounded-lg">
                        <Bed className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">Dementia Care</h3>
                        <p className="text-sm text-gray-500">Total capacity: 8 beds</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBedAvailability(prev => ({
                          ...prev,
                          dementia_available: Math.max(0, prev.dementia_available - 1)
                        }))}
                        className="h-10 w-10 p-0"
                      >
                        -
                      </Button>
                      <Input
                        type="number"
                        min="0"
                        max="8"
                        value={bedAvailability.dementia_available}
                        onChange={(e) => setBedAvailability(prev => ({
                          ...prev,
                          dementia_available: Math.min(8, Math.max(0, parseInt(e.target.value) || 0))
                        }))}
                        className="w-20 text-center text-lg font-bold"
                      />
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBedAvailability(prev => ({
                          ...prev,
                          dementia_available: Math.min(8, prev.dementia_available + 1)
                        }))}
                        className="h-10 w-10 p-0"
                      >
                        +
                      </Button>
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-6 border-t">
                  <Button
                    onClick={updateBedAvailability}
                    disabled={bedLoading}
                    className="w-full bg-green-600 hover:bg-green-700"
                    data-testid="update-bed-availability-btn"
                  >
                    {bedLoading ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Update Availability
                      </>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* COMPLAINTS TAB */}
        {activeTab === "complaints" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Complaints Management</h2>
              <Button 
                onClick={fetchComplaints} 
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>

            {/* Complaints List */}
            {complaintsLoading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-red-600 mb-4" />
                <p className="text-gray-600">Loading complaints...</p>
              </div>
            ) : complaints.length === 0 ? (
              <Card className="border-2 border-gray-200">
                <CardContent className="pt-8 pb-8 text-center">
                  <AlertTriangle className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No complaints found.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {complaints.map((complaint) => (
                  <Card 
                    key={complaint.id} 
                    className="border-2 border-red-200 hover:border-red-300 transition-colors"
                    data-testid={`complaint-card-${complaint.id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        {/* Complaint Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Badge className="bg-red-500 text-white">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {complaint.complaint_type}
                            </Badge>
                            <Badge className={
                              complaint.status === "new" 
                                ? "bg-orange-100 text-orange-700" 
                                : complaint.status === "investigating"
                                ? "bg-blue-100 text-blue-700"
                                : "bg-green-100 text-green-700"
                            }>
                              {complaint.status || "New"}
                            </Badge>
                            <span className="text-sm text-gray-500">
                              {formatDate(complaint.created_at)}
                            </span>
                          </div>

                          <div className="grid md:grid-cols-2 gap-4 mb-4">
                            <div className="flex items-center gap-2">
                              <User className="h-4 w-4 text-red-600" />
                              <span className="font-semibold">{complaint.name}</span>
                            </div>
                            <div className="flex items-center gap-2">
                              <Phone className="h-4 w-4 text-red-600" />
                              <a href={`tel:${complaint.phone}`} className="text-red-600 hover:underline">
                                {complaint.phone}
                              </a>
                            </div>
                            <div className="flex items-center gap-2">
                              <Mail className="h-4 w-4 text-red-600" />
                              <a href={`mailto:${complaint.email}`} className="text-red-600 hover:underline">
                                {complaint.email}
                              </a>
                            </div>
                            {complaint.relationship && (
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4 text-red-600" />
                                <span>{complaint.relationship}</span>
                              </div>
                            )}
                          </div>

                          <div className="bg-red-50 rounded-lg p-4 border-l-4 border-red-500">
                            <p className="text-sm text-red-700 font-medium mb-1">Complaint:</p>
                            <p className="text-gray-700 text-sm">{complaint.complaint}</p>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex lg:flex-col gap-2">
                          <a
                            href={`mailto:${complaint.email}?subject=Re: Your Complaint - Barnston Court Nursing Home`}
                            className="inline-flex items-center justify-center px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
                          >
                            <Mail className="h-4 w-4 mr-2" />
                            Reply
                          </a>
                          <a
                            href={`tel:${complaint.phone}`}
                            className="inline-flex items-center justify-center px-4 py-2 bg-orange-100 hover:bg-orange-200 text-orange-700 rounded-md transition-colors"
                          >
                            <Phone className="h-4 w-4 mr-2" />
                            Call
                          </a>
                          <Button
                            onClick={() => openDeleteModal(complaint, "complaint")}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* REVIEWS TAB */}
        {activeTab === "reviews" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-gray-900">Review Management</h2>
              <Button 
                onClick={fetchReviews} 
                variant="outline"
                className="flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
            </div>

            {/* Review Filter Tabs */}
            <div className="flex gap-2 mb-6">
              <Button
                onClick={() => setReviewFilter("pending")}
                className={reviewFilter === "pending" 
                  ? "bg-orange-500 hover:bg-orange-600" 
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"}
              >
                <Clock className="h-4 w-4 mr-2" />
                Pending Reviews
              </Button>
              <Button
                onClick={() => setReviewFilter("approved")}
                className={reviewFilter === "approved" 
                  ? "bg-green-600 hover:bg-green-700" 
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"}
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Approved Reviews
              </Button>
            </div>

            {/* Reviews List */}
            {loading ? (
              <div className="text-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin mx-auto text-teal-600 mb-4" />
                <p className="text-gray-600">Loading reviews...</p>
              </div>
            ) : reviews.length === 0 ? (
              <Card className="border-2 border-gray-200">
                <CardContent className="pt-8 pb-8 text-center">
                  <Star className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <p className="text-gray-600">No {reviewFilter} reviews found.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {reviews.map((review) => (
                  <Card 
                    key={review.id} 
                    className="border-2 border-gray-200 hover:border-teal-300 transition-colors"
                    data-testid={`review-card-${review.id}`}
                  >
                    <CardContent className="pt-6">
                      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                        {/* Review Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-3">
                            <Badge className={
                              review.status === "pending" 
                                ? "bg-orange-100 text-orange-700" 
                                : "bg-green-100 text-green-700"
                            }>
                              {review.status === "pending" ? "Pending" : "Approved"}
                            </Badge>
                            <div className="flex items-center gap-1">
                              {[...Array(10)].map((_, i) => (
                                <Star
                                  key={i}
                                  className={`h-4 w-4 ${
                                    i < review.rating
                                      ? "fill-orange-500 text-orange-500"
                                      : "text-gray-300"
                                  }`}
                                />
                              ))}
                              <span className="ml-2 font-semibold">{review.rating}/10</span>
                            </div>
                          </div>

                          <h3 className="font-bold text-lg text-gray-900 mb-2">
                            {review.review_title}
                          </h3>
                          
                          <p className="text-gray-700 mb-4 leading-relaxed">
                            {review.review_text}
                          </p>

                          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-500">
                            <span className="flex items-center gap-1">
                              <User className="h-4 w-4" />
                              {review.reviewer_name}
                            </span>
                            <span className="flex items-center gap-1">
                              <Mail className="h-4 w-4" />
                              {review.reviewer_email}
                            </span>
                            <span>{review.relationship}</span>
                            <span>{review.care_type}</span>
                            <span>Stay: {review.stay_duration}</span>
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex lg:flex-col gap-2">
                          {reviewFilter === "pending" && (
                            <>
                              <Button
                                onClick={() => handleApprove(review.id)}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                data-testid={`approve-btn-${review.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Approve
                              </Button>
                              <Button
                                onClick={() => handleReject(review.id)}
                                variant="outline"
                                className="border-red-500 text-red-500 hover:bg-red-50"
                                data-testid={`reject-btn-${review.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Reject
                              </Button>
                            </>
                          )}
                          {reviewFilter === "approved" && (
                            <Button
                              onClick={() => handleDisapprove(review.id)}
                              variant="outline"
                              className="border-orange-500 text-orange-600 hover:bg-orange-50"
                              data-testid={`disapprove-btn-${review.id}`}
                            >
                              <XCircle className="h-4 w-4 mr-2" />
                              Disapprove
                            </Button>
                          )}
                          <Button
                            onClick={() => openDeleteModal(review, "review")}
                            variant="outline"
                            className="border-red-300 text-red-600 hover:bg-red-50"
                            data-testid={`delete-review-btn-${review.id}`}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Reply Modal */}
      <Dialog open={replyModalOpen} onOpenChange={setReplyModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-teal-700">
              Reply to Enquiry
            </DialogTitle>
          </DialogHeader>
          
          {selectedEnquiry && (
            <div className="space-y-4">
              {/* Enquiry Summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>Name:</strong> {selectedEnquiry.name}</div>
                  <div><strong>Email:</strong> {selectedEnquiry.email}</div>
                  <div><strong>Phone:</strong> {selectedEnquiry.phone}</div>
                  <div><strong>Type:</strong> {getEnquiryTypeInfo(selectedEnquiry.enquiry_type).label}</div>
                </div>
                {selectedEnquiry.message && (
                  <div className="mt-3 pt-3 border-t">
                    <strong className="text-sm">Original Message:</strong>
                    <p className="text-sm text-gray-600 mt-1">{selectedEnquiry.message}</p>
                  </div>
                )}
              </div>

              {/* Reply Form */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Reply
                </label>
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply here..."
                  rows={6}
                  className="w-full"
                />
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3">
                <Button
                  variant="outline"
                  onClick={() => setReplyModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSendReply}
                  disabled={sendingReply}
                  className="bg-teal-600 hover:bg-teal-700"
                >
                  {sendingReply ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Reply
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onOpenChange={setDeleteModalOpen}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-6 w-6" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <p className="text-gray-700">
              Are you sure you want to delete this {deleteType}? This action cannot be undone.
            </p>
            
            {itemToDelete && (
              <div className="bg-red-50 rounded-lg p-3 text-sm">
                <p><strong>Name:</strong> {itemToDelete.name || itemToDelete.reviewer_name}</p>
                <p><strong>Email:</strong> {itemToDelete.email || itemToDelete.reviewer_email}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setDeleteModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPanel;
